import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash, Eye, EyeSlash, UploadSimple, Spinner, X, Barcode } from "@phosphor-icons/react";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api, authHeaders } from "@/lib/api";
import PageHeader from "@/admin/components/PageHeader";
import ScannerDialog from "@/admin/components/ScannerDialog";
import { useAdmin } from "@/context/AdminContext";

const DEFAULT_CATEGORIES = [
    "sandwiches",
    "club-sandwiches",
    "grinders",
    "wraps",
    "salads",
    "fries",
    "mofongos",
    "rice-and-beans",
    "made-to-order",
    "dominican",
    "empanadas",
    "juices",
    "drinks",
];
const NEW_CATEGORY_TOKEN = "__new__";

const formatCat = (c) =>
    c
        .split("-")
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(" ");

const blank = {
    category: "sandwiches",
    name_en: "",
    name_es: "",
    desc_en: "",
    desc_es: "",
    price: 0,
    image: "",
    tag: "",
    barcode: "",
    available: true,
    taxable: true,
};

export default function MenuManager() {
    const { isOwner } = useAdmin();
    const [items, setItems] = useState([]);
    const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
    const [editing, setEditing] = useState(null); // null | item
    const [form, setForm] = useState(blank);
    const [newCategory, setNewCategory] = useState("");
    const [showNewCategory, setShowNewCategory] = useState(false);
    const [busy, setBusy] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [dragOver, setDragOver] = useState(false);
    const [scannerOpen, setScannerOpen] = useState(false);
    const fileInputRef = useRef(null);

    const fetchItems = () =>
        api.get("/admin/menu", { headers: authHeaders() })
            .then((r) => setItems(r.data.items))
            .catch(() => toast.error("Couldn't load menu"));

    const fetchCategories = () =>
        api.get("/admin/menu/categories", { headers: authHeaders() })
            .then((r) => {
                const found = new Set([...DEFAULT_CATEGORIES, ...(r.data.categories || [])]);
                const ordered = DEFAULT_CATEGORIES.filter((c) => found.has(c));
                const extras = Array.from(found)
                    .filter((c) => !DEFAULT_CATEGORIES.includes(c))
                    .sort();
                setCategories([...ordered, ...extras]);
            })
            .catch(() => {});

    useEffect(() => {
        fetchItems();
        fetchCategories();
    }, []);

    const openCreate = () => {
        setEditing({});
        setForm(blank);
        setShowNewCategory(false);
        setNewCategory("");
    };

    const openEdit = (it) => {
        setEditing(it);
        setForm({ ...blank, ...it });
        setShowNewCategory(false);
        setNewCategory("");
    };

    const onCategoryChange = (val) => {
        if (val === NEW_CATEGORY_TOKEN) {
            setShowNewCategory(true);
            setNewCategory("");
        } else {
            setShowNewCategory(false);
            setForm({ ...form, category: val });
        }
    };

    const uploadFile = async (file) => {
        if (!file) return;
        if (!file.type.startsWith("image/")) {
            toast.error("Eso no es una imagen");
            return;
        }
        if (file.size > 25 * 1024 * 1024) {
            toast.error("Máximo 25 MB. Usa una imagen más pequeña.");
            return;
        }
        const fd = new FormData();
        fd.append("file", file);
        fd.append("purpose", "menu");
        setUploading(true);
        try {
            const r = await api.post("/admin/upload/image", fd, {
                headers: { ...authHeaders(), "Content-Type": "multipart/form-data" },
            });
            setForm((f) => ({ ...f, image: r.data.url }));
            toast.success(
                `Subida ✓ · ${r.data.width}×${r.data.height} · ${Math.round(r.data.size_kb)} KB`,
            );
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Error al subir");
        } finally {
            setUploading(false);
        }
    };

    const onDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        const f = e.dataTransfer?.files?.[0];
        if (f) uploadFile(f);
    };

    const onPickFile = (e) => {
        const f = e.target.files?.[0];
        if (f) uploadFile(f);
        e.target.value = "";
    };

    const save = async () => {
        // If user entered a brand new category, use it
        let finalCategory = form.category;
        if (showNewCategory) {
            // Normalize: lowercase, only alphanumerics/dashes, no leading/trailing
            // dashes, no double dashes. Prevents broken slugs like "-fresh-sandwiches-".
            const cleaned = newCategory
                .trim()
                .toLowerCase()
                .replace(/[^a-z0-9]+/g, "-")
                .replace(/-+/g, "-")
                .replace(/^-+|-+$/g, "");
            if (!cleaned) {
                toast.error("Escribe el nombre de la categoría");
                return;
            }
            finalCategory = cleaned;
        }
        if (!form.name_en || !form.name_es || !form.image) {
            toast.error("Name (EN/ES) and image are required");
            return;
        }
        setBusy(true);
        try {
            const payload = { ...form, category: finalCategory, price: Number(form.price) };
            if (editing?.id) {
                await api.put(`/admin/menu/${editing.id}`, payload, { headers: authHeaders() });
                toast.success("Item updated");
            } else {
                await api.post("/admin/menu", payload, { headers: authHeaders() });
                toast.success("Item created");
            }
            setEditing(null);
            setShowNewCategory(false);
            setNewCategory("");
            fetchItems();
            fetchCategories();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        } finally {
            setBusy(false);
        }
    };

    const toggleAvail = async (it) => {
        try {
            await api.put(
                `/admin/menu/${it.id}/availability`,
                { available: !it.available },
                { headers: authHeaders() },
            );
            toast.success(!it.available ? "Now available" : "Marked sold out");
            fetchItems();
        } catch {
            toast.error("Couldn't update");
        }
    };

    const doDelete = async () => {
        if (!confirmDelete) return;
        try {
            await api.delete(`/admin/menu/${confirmDelete.id}`, { headers: authHeaders() });
            toast.success("Deleted");
            setConfirmDelete(null);
            fetchItems();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Delete failed");
        }
    };

    return (
        <div data-testid="admin-menu">
            <PageHeader
                kicker="Catalog"
                title="Menu"
                action={
                    isOwner && (
                        <Button
                            data-testid="menu-create-btn"
                            onClick={openCreate}
                            className="rounded-full bg-primary hover:bg-primary/90 font-bold uppercase tracking-widest"
                        >
                            <Plus size={16} weight="bold" className="mr-1.5" /> New item
                        </Button>
                    )
                }
            />

            {/* Desktop table — hidden on small screens where we render cards instead. */}
            <div className="hidden md:block overflow-x-auto bg-white rounded-2xl border border-border">
                <table className="w-full text-sm" data-testid="menu-table">
                    <thead className="bg-muted/40">
                        <tr className="text-left text-[11px] uppercase tracking-widest text-muted-foreground">
                            <th className="px-4 py-3">Item</th>
                            <th className="px-4 py-3">Category</th>
                            <th className="px-4 py-3">Price</th>
                            <th className="px-4 py-3">Available</th>
                            <th className="px-4 py-3"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((it) => (
                            <tr
                                key={it.id}
                                data-testid={`menu-row-${it.id}`}
                                className="border-t border-border align-middle"
                            >
                                <td className="px-4 py-3 flex items-center gap-3 min-w-[260px]">
                                    <img src={it.image} alt="" className="w-12 h-12 object-cover rounded-lg" />
                                    <div className="min-w-0">
                                        <p className="font-semibold truncate">{it.name_en}</p>
                                        <p className="text-xs text-muted-foreground truncate">{it.name_es}</p>
                                    </div>
                                </td>
                                <td className="px-4 py-3 capitalize">{it.category}</td>
                                <td className="px-4 py-3 font-bold">${it.price.toFixed(2)}</td>
                                <td className="px-4 py-3">
                                    <button
                                        data-testid={`toggle-avail-${it.id}`}
                                        onClick={() => toggleAvail(it)}
                                        className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest ${
                                            it.available
                                                ? "bg-emerald-100 text-emerald-900"
                                                : "bg-zinc-200 text-zinc-700"
                                        }`}
                                    >
                                        {it.available ? <Eye size={12} weight="bold" /> : <EyeSlash size={12} weight="bold" />}
                                        {it.available ? "Active" : "86'd"}
                                    </button>
                                </td>
                                <td className="px-4 py-3 text-right whitespace-nowrap">
                                    {isOwner && (
                                        <>
                                            <button
                                                data-testid={`edit-${it.id}`}
                                                onClick={() => openEdit(it)}
                                                className="inline-flex h-8 w-8 items-center justify-center rounded-full hover:bg-muted"
                                            >
                                                <Pencil size={14} weight="bold" />
                                            </button>
                                            <button
                                                data-testid={`delete-${it.id}`}
                                                onClick={() => setConfirmDelete(it)}
                                                className="inline-flex h-8 w-8 items-center justify-center rounded-full hover:bg-red-50 text-destructive"
                                            >
                                                <Trash size={14} weight="bold" />
                                            </button>
                                        </>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Mobile card list — same data, better ergonomics on small screens. */}
            <div className="md:hidden space-y-2" data-testid="menu-cards">
                {items.map((it) => (
                    <article
                        key={it.id}
                        data-testid={`menu-card-${it.id}`}
                        className="bg-white rounded-2xl border border-border p-3 flex gap-3"
                    >
                        <img src={it.image} alt="" className="w-16 h-16 object-cover rounded-lg shrink-0" />
                        <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                                <div className="min-w-0">
                                    <p className="font-bold text-sm truncate">{it.name_en}</p>
                                    <p className="text-xs text-muted-foreground truncate">{it.name_es}</p>
                                </div>
                                <span className="text-sm font-bold whitespace-nowrap">${it.price.toFixed(2)}</span>
                            </div>
                            <p className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1 capitalize">
                                {it.category}
                            </p>
                            <div className="flex items-center gap-2 mt-2 flex-wrap">
                                <button
                                    data-testid={`m-toggle-avail-${it.id}`}
                                    onClick={() => toggleAvail(it)}
                                    className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest ${
                                        it.available
                                            ? "bg-emerald-100 text-emerald-900"
                                            : "bg-zinc-200 text-zinc-700"
                                    }`}
                                >
                                    {it.available ? <Eye size={10} weight="bold" /> : <EyeSlash size={10} weight="bold" />}
                                    {it.available ? "Activo" : "86'd"}
                                </button>
                                {isOwner && (
                                    <>
                                        <button
                                            data-testid={`m-edit-${it.id}`}
                                            onClick={() => openEdit(it)}
                                            className="ml-auto h-8 w-8 grid place-items-center rounded-full bg-muted"
                                        >
                                            <Pencil size={12} weight="bold" />
                                        </button>
                                        <button
                                            data-testid={`m-delete-${it.id}`}
                                            onClick={() => setConfirmDelete(it)}
                                            className="h-8 w-8 grid place-items-center rounded-full bg-red-50 text-destructive"
                                        >
                                            <Trash size={12} weight="bold" />
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    </article>
                ))}
            </div>

            {/* Edit / create */}
            <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
                <DialogContent
                    data-testid="menu-edit-dialog"
                    className="max-w-lg max-h-[90vh] overflow-y-auto"
                >
                    <DialogHeader>
                        <DialogTitle className="font-display font-black text-2xl">
                            {editing?.id ? "Edit item" : "New item"}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <label className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold block mb-1">
                                Categoría
                            </label>
                            <select
                                data-testid="form-category"
                                value={showNewCategory ? NEW_CATEGORY_TOKEN : form.category}
                                onChange={(e) => onCategoryChange(e.target.value)}
                                className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                            >
                                {categories.map((c) => (
                                    <option key={c} value={c}>
                                        {formatCat(c)}
                                    </option>
                                ))}
                                <option value={NEW_CATEGORY_TOKEN}>
                                    ➕ Nueva categoría…
                                </option>
                            </select>
                            {showNewCategory && (
                                <div className="mt-2 flex gap-2">
                                    <Input
                                        data-testid="form-new-category"
                                        placeholder="Ej: Tacos, Postres, Desayunos…"
                                        value={newCategory}
                                        onChange={(e) => setNewCategory(e.target.value)}
                                        autoFocus
                                    />
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={() => {
                                            setShowNewCategory(false);
                                            setNewCategory("");
                                        }}
                                        className="rounded-md"
                                    >
                                        Cancelar
                                    </Button>
                                </div>
                            )}
                            <p className="text-[10px] text-muted-foreground mt-1.5">
                                Las nuevas categorías aparecen automáticamente en el menú público.
                            </p>
                        </div>
                        <Input
                            data-testid="form-name-en"
                            placeholder="Name (English)"
                            value={form.name_en}
                            onChange={(e) => setForm({ ...form, name_en: e.target.value })}
                        />
                        <Input
                            data-testid="form-name-es"
                            placeholder="Nombre (Español)"
                            value={form.name_es}
                            onChange={(e) => setForm({ ...form, name_es: e.target.value })}
                        />
                        <Textarea
                            data-testid="form-desc-en"
                            placeholder="Description (English)"
                            rows={2}
                            value={form.desc_en}
                            onChange={(e) => setForm({ ...form, desc_en: e.target.value })}
                        />
                        <Textarea
                            data-testid="form-desc-es"
                            placeholder="Descripción (Español)"
                            rows={2}
                            value={form.desc_es}
                            onChange={(e) => setForm({ ...form, desc_es: e.target.value })}
                        />
                        <div className="grid grid-cols-2 gap-3">
                            <Input
                                data-testid="form-price"
                                type="number"
                                step="0.01"
                                placeholder="Price"
                                value={form.price}
                                onChange={(e) => setForm({ ...form, price: e.target.value })}
                            />
                            <Input
                                data-testid="form-tag"
                                placeholder="Tag (Best Seller, Spicy…)"
                                value={form.tag}
                                onChange={(e) => setForm({ ...form, tag: e.target.value })}
                            />
                        </div>
                        <div>
                            <label className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold block mb-1">
                                Código de barras / QR (opcional)
                            </label>
                            <div className="flex gap-2">
                                <Input
                                    data-testid="form-barcode"
                                    placeholder="Escanea o escribe el código…"
                                    value={form.barcode || ""}
                                    onChange={(e) => setForm({ ...form, barcode: e.target.value })}
                                    autoComplete="off"
                                    className="flex-1"
                                />
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => setScannerOpen(true)}
                                    data-testid="form-barcode-scan-btn"
                                    className="shrink-0 gap-2"
                                >
                                    <Barcode size={18} weight="bold" />
                                    Escanear
                                </Button>
                            </div>
                            <p className="text-[10px] text-muted-foreground mt-1">
                                Apunta tu escáner o pulsa Escanear para usar la cámara. Útil para artículos empaquetados.
                            </p>
                        </div>
                        <div>
                            <label className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold block mb-1">
                                Imagen del platillo
                            </label>
                            <div
                                data-testid="menu-image-dropzone"
                                onDragOver={(e) => {
                                    e.preventDefault();
                                    setDragOver(true);
                                }}
                                onDragLeave={() => setDragOver(false)}
                                onDrop={onDrop}
                                onClick={() => !uploading && fileInputRef.current?.click()}
                                className={`relative cursor-pointer rounded-2xl border-2 border-dashed p-5 text-center transition ${
                                    dragOver
                                        ? "border-primary bg-primary/5"
                                        : "border-border hover:border-primary/60 hover:bg-muted/40"
                                }`}
                            >
                                <input
                                    ref={fileInputRef}
                                    data-testid="menu-image-file-input"
                                    type="file"
                                    accept="image/jpeg,image/png,image/webp"
                                    onChange={onPickFile}
                                    className="hidden"
                                />
                                {uploading ? (
                                    <>
                                        <Spinner size={24} className="mx-auto text-primary animate-spin" weight="bold" />
                                        <p className="mt-2 text-sm font-bold uppercase tracking-widest text-primary">
                                            Subiendo y optimizando…
                                        </p>
                                    </>
                                ) : (
                                    <>
                                        <UploadSimple
                                            size={24}
                                            weight="bold"
                                            className="mx-auto text-foreground/60"
                                        />
                                        <p className="mt-2 text-sm font-bold tracking-tight">
                                            Arrastra y suelta tu imagen aquí
                                        </p>
                                        <p className="text-xs text-muted-foreground mt-0.5">
                                            o haz clic para elegir · JPG / PNG / WebP · máx 25 MB
                                        </p>
                                    </>
                                )}
                            </div>
                            <div className="flex items-center gap-3 mt-3">
                                <span className="h-px bg-border flex-1" />
                                <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">
                                    o pega una URL
                                </span>
                                <span className="h-px bg-border flex-1" />
                            </div>
                            <Input
                                data-testid="form-image"
                                placeholder="https://…"
                                value={form.image}
                                onChange={(e) => setForm({ ...form, image: e.target.value })}
                                className="mt-3"
                            />
                        </div>
                        {form.image && (
                            <div className="relative">
                                <img
                                    src={form.image}
                                    alt=""
                                    className="w-full h-40 object-cover rounded-xl border border-border"
                                />
                                <button
                                    type="button"
                                    data-testid="menu-image-remove"
                                    onClick={() => setForm({ ...form, image: "" })}
                                    className="absolute top-2 right-2 h-7 w-7 inline-flex items-center justify-center rounded-full bg-black/70 text-white hover:bg-black"
                                    aria-label="Quitar imagen"
                                >
                                    <X size={14} weight="bold" />
                                </button>
                            </div>
                        )}
                        <label className="flex items-center gap-2 text-sm font-semibold">
                            <input
                                data-testid="form-available"
                                type="checkbox"
                                checked={form.available}
                                onChange={(e) => setForm({ ...form, available: e.target.checked })}
                            />
                            Available
                        </label>
                        <label className="flex items-center gap-2 text-sm font-semibold">
                            <input
                                data-testid="form-taxable"
                                type="checkbox"
                                checked={form.taxable !== false}
                                onChange={(e) => setForm({ ...form, taxable: e.target.checked })}
                            />
                            Taxable (7.35% sales tax)
                        </label>
                    </div>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setEditing(null)}
                            data-testid="form-cancel"
                            className="rounded-full"
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={save}
                            disabled={busy}
                            data-testid="form-save"
                            className="rounded-full bg-primary hover:bg-primary/90 font-bold uppercase tracking-widest"
                        >
                            {busy ? "..." : "Save"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete confirm */}
            <Dialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
                <DialogContent data-testid="delete-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display font-black">Delete item?</DialogTitle>
                    </DialogHeader>
                    <p className="text-sm text-muted-foreground">
                        This permanently removes <strong>{confirmDelete?.name_en}</strong>. Already-placed orders are unaffected.
                    </p>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setConfirmDelete(null)}
                            data-testid="delete-cancel"
                            className="rounded-full"
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={doDelete}
                            data-testid="delete-confirm"
                            className="rounded-full bg-destructive hover:bg-destructive/90 font-bold uppercase tracking-widest"
                        >
                            Delete
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Camera/hardware scanner for barcode field */}
            <ScannerDialog
                open={scannerOpen}
                onClose={() => setScannerOpen(false)}
                onCode={(code) => {
                    setForm((f) => ({ ...f, barcode: code }));
                    setScannerOpen(false);
                    toast.success(`Código capturado: ${code}`);
                }}
            />
        </div>
    );
}
